import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { isFinnishRegistrationNumber } from '../../../utils/registration';

/**
 * Hook for vehicle lookup functionality with explicit button-triggered lookup and quota awareness.
 *
 * @param {Object} options - Hook options
 * @param {Function} options.t - Translation function
 * @param {Function} options.onVehicleFound - Callback when vehicle is found
 * @returns {Object} Vehicle lookup state and handlers
 */
export function useVehicleLookup({ t, onVehicleFound, onCustomerFound }) {
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [vehicleLookedUp, setVehicleLookedUp] = useState(false);
  const [showTrialInfo, setShowTrialInfo] = useState(false);
  const [showNonFinnishInfo, setShowNonFinnishInfo] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [trialRestricted, setTrialRestricted] = useState(false);

  const lookupAbortControllerRef = useRef(null);
  const pendingRegNumberRef = useRef(null);
  const autoSearchTimerRef = useRef(null);

  /**
   * Fetch quota info from backend.
   */
  const fetchQuota = useCallback(async () => {
    try {
      const response = await axios.get('/api/vehicles/lookup-quota/');
      setQuotaInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch lookup quota:', error);
    }
  }, []);

  // Fetch quota on mount
  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  /**
   * Search for existing vehicle in organization database.
   */
  const searchExistingVehicle = useCallback(async (regNumber) => {
    if (!regNumber || regNumber.length < 3) {
      return null;
    }

    try {
      const response = await axios.get(`/api/vehicles/by-registration/?registration_number=${encodeURIComponent(regNumber)}`, {
        signal: lookupAbortControllerRef.current?.signal,
      });

      if (response.data.found && response.data.vehicle) {
        return { vehicle: response.data.vehicle, lastCustomer: response.data.last_customer || null };
      }
      return null;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return null;
      }
      console.error('Vehicle search failed:', error);
      return null;
    }
  }, []);

  /**
   * Perform the actual vehicle lookup API call.
   */
  const lookupVehicle = useCallback(async (regNumber) => {
    if (!regNumber) return;

    // Cancel any pending auto-search debounce
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = null;
    }

    // Cancel any previous lookup request
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    lookupAbortControllerRef.current = new AbortController();

    setLookupLoading(true);
    setLookupError(null);
    setShowTrialInfo(false);
    setQuotaExceeded(false);
    setRequiresConfirmation(false);
    setTrialRestricted(false);
    pendingRegNumberRef.current = regNumber;

    try {
      // First check if vehicle exists in org database (free)
      const result = await searchExistingVehicle(regNumber);
      if (result) {
        onVehicleFound?.(result.vehicle);
        if (result.lastCustomer) {
          onCustomerFound?.(result.lastCustomer);
        }
        // Only mark as "looked up" if the vehicle actually has data
        const hasData = result.vehicle.manufacturer || result.vehicle.model || result.vehicle.vin;
        if (hasData) {
          setVehicleLookedUp(true);
          setLookupLoading(false);
          return;
        }
        // Vehicle exists but has no data — fall through to external lookup
      }

      // Not in org DB and not Finnish pattern - show info
      if (!isFinnishRegistrationNumber(regNumber)) {
        setShowNonFinnishInfo(true);
        setLookupLoading(false);
        return;
      }

      // Finnish pattern - try external lookup
      const response = await axios.post('/api/vehicles/lookup/', {
        registration_number: regNumber.toUpperCase(),
      }, {
        signal: lookupAbortControllerRef.current.signal,
      });

      if (response.data.success && response.data.vehicle) {
        onVehicleFound?.(response.data.vehicle);
        setVehicleLookedUp(true);
        // Refresh quota after successful lookup
        fetchQuota();
      } else {
        setLookupError(response.data.error || t('Vehicle not found'));
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }

      if (error.response?.status === 402) {
        const data = error.response.data;
        setQuotaExceeded(true);
        setRequiresConfirmation(!!data.requires_confirmation);
        setTrialRestricted(!!data.trial_restricted);
        if (data.quota_info) {
          setQuotaInfo(data.quota_info);
        }
        if (data.trial_restricted) {
          setShowTrialInfo(true);
        }
        return;
      }

      if (error.response?.status === 429) {
        setLookupError(t('vehicleLookup.rateLimited'));
        return;
      }

      if (error.response?.status === 503) {
        setLookupError(t('vehicleLookup.serviceError'));
        return;
      }

      console.error('Vehicle lookup failed:', error);
      setLookupError(t('Vehicle lookup failed'));
    } finally {
      setLookupLoading(false);
    }
  }, [t, onVehicleFound, onCustomerFound, searchExistingVehicle, fetchQuota]);

  /**
   * Perform lookup with confirm_extra=true (for over-quota paid users).
   */
  const lookupWithConfirm = useCallback(async (regNumber) => {
    const reg = regNumber || pendingRegNumberRef.current;
    if (!reg) return;

    // Cancel any previous lookup request
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    lookupAbortControllerRef.current = new AbortController();

    setLookupLoading(true);
    setLookupError(null);
    setQuotaExceeded(false);
    setRequiresConfirmation(false);
    setTrialRestricted(false);

    try {
      const response = await axios.post('/api/vehicles/lookup/', {
        registration_number: reg.toUpperCase(),
        confirm_extra: true,
      }, {
        signal: lookupAbortControllerRef.current.signal,
      });

      if (response.data.success && response.data.vehicle) {
        onVehicleFound?.(response.data.vehicle);
        setVehicleLookedUp(true);
        fetchQuota();
      } else {
        setLookupError(response.data.error || t('Vehicle not found'));
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Vehicle lookup with confirm failed:', error);
      setLookupError(t('Vehicle lookup failed'));
    } finally {
      setLookupLoading(false);
    }
  }, [t, onVehicleFound, fetchQuota]);

  /**
   * Handle registration number change — cleans value and debounced auto-search
   * for existing vehicles in org database (free, no quota impact).
   * Returns the cleaned value synchronously.
   */
  const handleRegistrationChange = useCallback((value) => {
    const cleanValue = value.replace(/\s+/g, '').trim().toUpperCase();

    setVehicleLookedUp(false);
    setLookupError(null);
    setShowNonFinnishInfo(false);
    setShowTrialInfo(false);
    setQuotaExceeded(false);
    setRequiresConfirmation(false);
    setTrialRestricted(false);

    // Cancel previous debounce and request
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = null;
    }
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }

    // Debounced auto-search for existing vehicles in org DB (free)
    if (cleanValue.length >= 3) {
      lookupAbortControllerRef.current = new AbortController();
      autoSearchTimerRef.current = setTimeout(async () => {
        const result = await searchExistingVehicle(cleanValue);
        if (result) {
          onVehicleFound?.(result.vehicle);
          if (result.lastCustomer) {
            onCustomerFound?.(result.lastCustomer);
          }
          // Only mark as "looked up" if the vehicle actually has data
          const hasData = result.vehicle.manufacturer || result.vehicle.model || result.vehicle.vin;
          if (hasData) {
            setVehicleLookedUp(true);
          }
        }
      }, 500);
    }

    return cleanValue;
  }, [searchExistingVehicle, onVehicleFound, onCustomerFound]);

  /**
   * Interrupt lookup and allow manual entry.
   */
  const interruptLookup = useCallback(() => {
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
      lookupAbortControllerRef.current = null;
    }
    setLookupLoading(false);
    setLookupError(null);
  }, []);

  /**
   * Cancel any pending lookup (use before form submit).
   */
  const cancelPendingLookup = useCallback(() => {
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
      lookupAbortControllerRef.current = null;
    }
    setLookupLoading(false);
  }, []);

  /**
   * Reset all lookup state.
   */
  const resetLookup = useCallback(() => {
    setVehicleLookedUp(false);
    setLookupError(null);
    setShowNonFinnishInfo(false);
    setShowTrialInfo(false);
    setLookupLoading(false);
    setQuotaExceeded(false);
    setRequiresConfirmation(false);
    setTrialRestricted(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
      if (lookupAbortControllerRef.current) {
        lookupAbortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    lookupLoading,
    lookupError,
    vehicleLookedUp,
    showTrialInfo,
    showNonFinnishInfo,
    quotaInfo,
    quotaExceeded,
    requiresConfirmation,
    trialRestricted,

    // Actions
    handleRegistrationChange,
    lookupVehicle,
    lookupWithConfirm,
    searchExistingVehicle,
    fetchQuota,
    interruptLookup,
    cancelPendingLookup,
    resetLookup,
  };
}

export default useVehicleLookup;
